import { useState, useEffect, useCallback, useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  RiFileTextLine,
  RiSurveyLine,
  RiListCheck2,
  RiQuestionnaireLine,
  RiFeedbackLine,
  RiContactsLine,
  RiUserLine,
  RiStoreLine,
  RiShoppingCartLine,
  RiServiceLine,
  RiSettingsLine,
  RiDashboardLine,
} from "react-icons/ri";
import {
  Type,
  Hash,
  Upload,
  Calendar,
  ToggleLeft,
  AlignLeft,
  ChevronDown,
  CheckSquare,
  Circle,
  GripVertical,
  Plus,
  FormInput,
  Trash2,
  Edit3,
} from "lucide-react";
import {
  fetchForms,
  createForm,
  fetchFormDetails,
  createFormField,
  createFieldOption,
  updateFieldOrder,
  updateFormField,
  deleteFormField,
} from "../utils/api";

const iconMap = {
  RiFileTextLine,
  RiSurveyLine,
  RiListCheck2,
  RiQuestionnaireLine,
  RiFeedbackLine,
  RiContactsLine,
  RiUserLine,
  RiStoreLine,
  RiShoppingCartLine,
  RiServiceLine,
  RiSettingsLine,
  RiDashboardLine,
};

const fieldTypeIcons = {
  text: <Type className="w-4 h-4" />,
  number: <Hash className="w-4 h-4" />,
  file: <Upload className="w-4 h-4" />,
  image: <Upload className="w-4 h-4" />,
  date: <Calendar className="w-4 h-4" />,
  time: <Calendar className="w-4 h-4" />,
  datetime: <Calendar className="w-4 h-4" />,
  toggle: <ToggleLeft className="w-4 h-4" />,
  textarea: <AlignLeft className="w-4 h-4" />,
  select: <ChevronDown className="w-4 h-4" />,
  "checkbox-group": <CheckSquare className="w-4 h-4" />,
  radio: <Circle className="w-4 h-4" />,
};

function FormIcon({ iconName, className = "w-6 h-6" }) {
  const IconComponent = iconMap[iconName];
  return IconComponent ? (
    <IconComponent className={className} />
  ) : (
    <RiFileTextLine className={className} />
  );
}

function DraggableFormField({ field, index, moveField, onDelete, onEdit }) {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "formField",
    item: () => ({ id: field._id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "formField",
    hover: (item, monitor) => {
      if (!ref.current) return;
      
      const dragIndex = item.index;
      const hoverIndex = index;
      
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) return;
      
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      // Only perform the move when the mouse has crossed half of the items height
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      
      // Time to actually perform the action
      moveField(dragIndex, hoverIndex);
      
      // Note: we're mutating the monitor item here!
      item.index = hoverIndex;
    },
  });

  // Connect drag and drop to the ref
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg cursor-move hover:shadow-md transition-all ${
        isDragging ? "opacity-50 scale-105 shadow-lg" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <GripVertical className="w-4 h-4 text-gray-400 drag-handle" />
        {fieldTypeIcons[field.type]}
        <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
          #{index + 1}
        </span>
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{field.name}</span>
          <span className="text-xs text-gray-500 capitalize">{field.type}</span>
        </div>
        {field.required && (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
            Required
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">{field.options?.length || 0} options</span>
        <button
          onClick={() => onEdit(field)}
          className="p-1 text-blue-500 hover:bg-blue-50 rounded transition-colors"
          aria-label="Edit field"
        >
          <Edit3 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(field._id)}
          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
          aria-label="Delete field"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function FormFieldsCanvas({ fields, setFields, selectedForm, onEditField }) {
  const moveField = useCallback(
    async (dragIndex, dropIndex) => {
      const clone = [...fields];
      const [moved] = clone.splice(dragIndex, 1);
      clone.splice(dropIndex, 0, moved);
      const updated = clone.map((f, i) => ({ ...f, order: i + 1 }));
      setFields(updated);
      
      try {
        await updateFieldOrder(
          selectedForm,
          updated.map((f) => ({ id: f._id, order: f.order }))
        );
      } catch (e) {
        console.error("Failed to update field order:", e);
        // Revert to original state if API call fails
        setFields(fields);
      }
    },
    [fields, selectedForm, setFields]
  );

  const deleteField = useCallback(
    async (id) => {
      try {
        await deleteFormField(id);
        const filtered = fields.filter((f) => f._id !== id);
        setFields(filtered.map((f, i) => ({ ...f, order: i + 1 })));
      } catch (err) {
        console.error("Error deleting field:", err);
        alert("Failed to delete field. Please try again.");
      }
    },
    [fields, setFields]
  );

  if (!selectedForm)
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 text-center text-gray-500">
          <FormInput className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Select a form to manage fields</p>
        </div>
      </div>
    );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Form Fields</h3>
        <p className="text-sm text-gray-500">Drag to reorder fields</p>
      </div>
      <div className="p-6 space-y-3 min-h-200">
        {fields.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No fields yet. Add your first field above.</p>
        ) : (
          fields.map((f, i) => (
            <DraggableFormField
              key={f._id}
              field={f}
              index={i}
              moveField={moveField}
              onDelete={deleteField}
              onEdit={onEditField}
            />
          ))
        )}
      </div>
    </div>
  );
}

function IconPicker({ selectedIcon, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50 transition-colors"
      >
        <FormIcon iconName={selectedIcon} />
        <ChevronDown className="w-4 h-4" />
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-64 grid grid-cols-4 gap-2">
          {Object.keys(iconMap).map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => {
                onSelect(icon);
                setIsOpen(false);
              }}
              className={`p-2 rounded-lg flex justify-center transition-colors ${
                selectedIcon === icon ? "bg-blue-100 text-blue-800" : "hover:bg-gray-100"
              }`}
            >
              <FormIcon iconName={icon} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FormBuilder() {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    enable: true,
    thank_you_message: "",
    icon: "RiFileTextLine",
  });

  const [fieldData, setFieldData] = useState({ name: "", type: "", required: false });
  const [optionData, setOptionData] = useState({ form_field_id: "", order: 0, value: "" });

  const fieldTypes = [
    { label: "Text Input", value: "text" },
    { label: "Number", value: "number" },
    { label: "Dropdown", value: "select" },
    { label: "Checkbox", value: "checkbox-group" },
    { label: "Radio", value: "radio" },
    { label: "Date", value: "date" },
    { label: "Time", value: "time" },
    { label: "Date & Time", value: "datetime" },
    { label: "File", value: "file" },
    { label: "Image", value: "image" },
    { label: "Toggle", value: "toggle" },
    { label: "Textarea", value: "textarea" },
  ];

  useEffect(() => {
    const loadForms = async () => {
      setLoading(true);
      setError(null);
      try {
        const formsData = await fetchForms();
        setForms(formsData);
      } catch (err) {
        setError("Failed to load forms");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadForms();
  }, []);

  useEffect(() => {
    if (!selectedForm) {
      setFields([]);
      return;
    }

    const loadFormDetails = async () => {
      setLoading(true);
      try {
        const { fields: formFields, options } = await fetchFormDetails(selectedForm);
        const sorted = formFields.sort((a, b) => a.order - b.order);
        setFields(
          sorted.map((f) => ({
            ...f,
            options: options.filter((o) => o.form_field_id === f._id),
          }))
        );
      } catch (err) {
        console.error("Failed to load form details:", err);
        setError("Failed to load form details");
      } finally {
        setLoading(false);
      }
    };

    loadFormDetails();
  }, [selectedForm]);

  const handleCreateForm = async () => {
    if (!formData.name) return;
    
    setLoading(true);
    try {
      const created = await createForm(formData);
      setForms((fs) => [...fs, created]);
      setFormData({ name: "", enable: true, thank_you_message: "", icon: "RiFileTextLine" });
      setSelectedForm(created._id);
    } catch (err) {
      console.error("Failed to create form:", err);
      setError("Failed to create form");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateField = async () => {
    if (!selectedForm || !fieldData.name || !fieldData.type) return;
    
    setLoading(true);
    try {
      const created = await createFormField({
        ...fieldData,
        form_id: selectedForm,
        order: fields.length + 1,
      });
      setFields((fs) => [...fs, { ...created, options: [] }]);
      setFieldData({ name: "", type: "", required: false });
    } catch (err) {
      console.error("Failed to create field:", err);
      setError("Failed to create field");
    } finally {
      setLoading(false);
    }
  };

  const handleEditField = (field) => {
    setEditingFieldId(field._id);
    setFieldData({ name: field.name, type: field.type, required: field.required });
  };

  const handleSaveField = async () => {
    if (!editingFieldId) return;
    
    setLoading(true);
    try {
      const updated = await updateFormField(editingFieldId, fieldData);
      setFields((fs) => fs.map((f) => (f._id === editingFieldId ? { ...updated, options: f.options } : f)));
      setEditingFieldId(null);
      setFieldData({ name: "", type: "", required: false });
    } catch (err) {
      console.error("Failed to update field:", err);
      setError("Failed to update field");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingFieldId(null);
    setFieldData({ name: "", type: "", required: false });
  };

  const needsOptions = ["select", "checkbox-group", "radio"];
  const handleCreateOption = async () => {
    if (!optionData.form_field_id || !optionData.value) return;
    
    setLoading(true);
    try {
      const created = await createFieldOption(optionData);
      setFields((fs) =>
        fs.map((f) => (f._id === created.form_field_id ? { ...f, options: [...f.options, created] } : f))
      );
      setOptionData({ form_field_id: "", order: 0, value: "" });
    } catch (err) {
      console.error("Failed to create option:", err);
      setError("Failed to create option");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-8 mx-auto">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
            <button
              onClick={() => setError(null)}
              className="float-right font-bold"
            >
              ×
            </button>
          </div>
        )}

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h2 className="text-xl font-semibold mb-4">Create / Edit Form</h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form Name</label>
              <input
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Form Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form Icon</label>
              <IconPicker
                selectedIcon={formData.icon}
                onSelect={(icon) => setFormData({ ...formData, icon })}
              />
            </div>
          </div>
          <button
            onClick={handleCreateForm}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            disabled={!formData.name || loading}
          >
            {loading ? "Creating..." : "Create Form"}
          </button>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Form to Edit</label>
            <select
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={selectedForm || ""}
              onChange={(e) => setSelectedForm(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Select Form --</option>
              {forms.map((f) => (
                <option key={f._id} value={f._id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedForm && (
          <>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 space-y-4">
              <h2 className="text-xl font-semibold">Add / Edit Field</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Field Name"
                  value={fieldData.name}
                  onChange={(e) => setFieldData({ ...fieldData, name: e.target.value })}
                  disabled={loading}
                />
                <select
                  className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={fieldData.type}
                  onChange={(e) => setFieldData({ ...fieldData, type: e.target.value })}
                  disabled={loading}
                >
                  <option value="">-- Type --</option>
                  {fieldTypes.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 p-2">
                  <input
                    type="checkbox"
                    checked={fieldData.required}
                    onChange={(e) => setFieldData({ ...fieldData, required: e.target.checked })}
                    disabled={loading}
                  />
                  Required
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={editingFieldId ? handleSaveField : handleCreateField}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                  disabled={!fieldData.name || !fieldData.type || loading}
                >
                  {loading ? "Saving..." : editingFieldId ? "Save Field" : "Add Field"}
                </button>
                {editingFieldId && (
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <FormFieldsCanvas
              fields={fields}
              setFields={setFields}
              selectedForm={selectedForm}
              onEditField={handleEditField}
            />

            {fields.some((f) => needsOptions.includes(f.type)) && (
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 space-y-4">
                <h2 className="text-xl font-semibold">Add Option</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <select
                    className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={optionData.form_field_id}
                    onChange={(e) => setOptionData({ ...optionData, form_field_id: e.target.value })}
                    disabled={loading}
                  >
                    <option value="">-- Field --</option>
                    {fields
                      .filter((f) => needsOptions.includes(f.type))
                      .map((f) => (
                        <option key={f._id} value={f._id}>
                          {f.name}
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Order"
                    value={optionData.order}
                    onChange={(e) => setOptionData({ ...optionData, order: +e.target.value })}
                    disabled={loading}
                    min="0"
                  />
                  <input
                    className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Option Value"
                    value={optionData.value}
                    onChange={(e) => setOptionData({ ...optionData, value: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <button
                  onClick={handleCreateOption}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 transition-colors"
                  disabled={!optionData.form_field_id || !optionData.value || loading}
                >
                  {loading ? "Adding..." : "Add Option"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DndProvider>
  );
}